/**
 * The single date format of the UI: dd.mm.yyyy (dzień.miesiąc.rok), with time as HH:mm.
 *
 * Use these helpers for every date shown to the user instead of rendering raw API values
 * ("2026-10-05" / "2026-10-05T11:26:00Z") or ad-hoc toLocaleDateString options
 * (`month: "long"` / `"short"` give "5 października 2026" / "5 paź 2026").
 *
 * Inputs:
 * - `yyyy-mm-dd` (DateField from the API) is read as a LOCAL calendar day, so it never
 *   shifts by the timezone offset;
 * - full ISO datetimes, epoch milliseconds and Date objects are shown in local time;
 * - empty/null/invalid → `fallback`;
 * - any other non-empty string (e.g. a label the backend already formatted) is returned
 *   unchanged — `new Date("05.10.2026")` would silently read it as 10 May.
 */

export type DateInput = string | number | Date | null | undefined;

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_PREFIX = /^\d{4}-\d{2}-\d{2}/;

const pad = (n: number) => String(n).padStart(2, "0");

/** Parses an API date value into a Date; null when it is empty or not a date. */
export function parseDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = value.trim();
  const dateOnly = ISO_DATE_ONLY.exec(s);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const local = new Date(Number(y), Number(m) - 1, Number(d));
    // Round-trip guards against overflow like 2026-02-31 silently becoming 03.03.
    return local.getMonth() === Number(m) - 1 && local.getDate() === Number(d) ? local : null;
  }
  if (!ISO_PREFIX.test(s)) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function format(value: DateInput, fallback: string, render: (d: Date) => string): string {
  const d = parseDate(value);
  if (d) return render(d);
  if (typeof value === "string" && value.trim() && !ISO_PREFIX.test(value.trim())) return value;
  return fallback;
}

/** "05.10.2026" */
export function formatDate(value: DateInput, fallback = "—"): string {
  return format(value, fallback, (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`);
}

/** "05.10.2026 13:26" */
export function formatDateTime(value: DateInput, fallback = "—"): string {
  return format(
    value,
    fallback,
    (d) =>
      `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
  );
}
